/**
 * DV-FF-001: Feature Flags React Hooks
 *
 * React Query hooks for feature flags management with optimistic updates
 * and structured error handling.
 */

import { useQuery, useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { featureFlagsAPI } from '@/lib/feature-flags-api';
import type {
  FeatureFlag,
  FeatureFlagStatus,
  FeatureFlagAuditEntry,
  FeatureFlagUpdate,
  FeatureFlagWithStatus
} from '@/types/feature-flags';

// Query keys for React Query
export const featureFlagsKeys = {
  all: ['feature-flags'] as const,
  lists: () => [...featureFlagsKeys.all, 'list'] as const,
  list: (filters?: any) => [...featureFlagsKeys.lists(), filters] as const,
  status: (name: string, brandId: string) => [...featureFlagsKeys.all, 'status', name, brandId] as const,
  audit: (flagName?: string) => [...featureFlagsKeys.all, 'audit', flagName] as const,
};

// Hook to fetch all feature flags
export function useFeatureFlags(): UseQueryResult<FeatureFlag[], Error> {
  return useQuery({
    queryKey: featureFlagsKeys.list(),
    queryFn: () => featureFlagsAPI.listFlags(),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Hook to fetch feature flags with status computation
export function useFeatureFlagsWithStatus(
  brandId: string = 'hi-si'
): UseQueryResult<FeatureFlagWithStatus[], Error> {
  return useQuery({
    queryKey: [...featureFlagsKeys.list(), 'with-status', brandId],
    queryFn: async (): Promise<FeatureFlagWithStatus[]> => {
      const flags = await featureFlagsAPI.listFlags();

      // Get status for each flag
      const flagsWithStatus = await Promise.all(
        flags.map(async (flag): Promise<FeatureFlagWithStatus> => {
          try {
            const status = await featureFlagsAPI.getFlagStatus(flag.name, brandId);
            return {
              ...flag,
              effective_status: status
            };
          } catch (error) {
            // Handle errors gracefully
            return {
              ...flag,
              effective_status: {
                enabled: false,
                reason: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
                flag_data: flag
              }
            };
          }
        })
      );

      return flagsWithStatus;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

// Hook to fetch single flag status
export function useFeatureFlagStatus(
  name: string,
  brandId: string = 'hi-si'
): UseQueryResult<FeatureFlagStatus, Error> {
  return useQuery({
    queryKey: featureFlagsKeys.status(name, brandId),
    queryFn: () => featureFlagsAPI.getFlagStatus(name, brandId),
    enabled: !!name,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

// Hook to fetch audit log
export function useFeatureFlagAuditLog(
  flagName?: string,
  limit: number = 50
): UseQueryResult<FeatureFlagAuditEntry[], Error> {
  return useQuery({
    queryKey: featureFlagsKeys.audit(flagName),
    queryFn: () => featureFlagsAPI.getAuditLog(flagName, limit),
    staleTime: 10 * 1000, // 10 seconds - audit log should be fresh
    gcTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Hook to update feature flag with optimistic updates
export function useUpdateFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (update: FeatureFlagUpdate) => featureFlagsAPI.updateFlag(update),
    onMutate: async (update: FeatureFlagUpdate) => {
      // Cancel outgoing queries for this flag
      await queryClient.cancelQueries({ queryKey: featureFlagsKeys.all });

      // Snapshot previous data for rollback
      const previousFlags = queryClient.getQueryData<FeatureFlag[]>(featureFlagsKeys.list());
      const previousFlagsWithStatus = queryClient.getQueryData<FeatureFlagWithStatus[]>([
        ...featureFlagsKeys.list(), 'with-status', 'hi-si'
      ]);

      // Optimistically update flag list
      if (previousFlags) {
        const updatedFlags = previousFlags.map(flag =>
          flag.name === update.name
            ? { ...flag, ...update, updated_at: new Date().toISOString() }
            : flag
        );
        queryClient.setQueryData(featureFlagsKeys.list(), updatedFlags);
      }

      // Optimistically update flags with status
      if (previousFlagsWithStatus) {
        const updatedFlagsWithStatus = previousFlagsWithStatus.map(flag =>
          flag.name === update.name
            ? {
                ...flag,
                ...update,
                updated_at: new Date().toISOString(),
                // Update effective status based on new values
                effective_status: {
                  ...flag.effective_status,
                  // Admin override takes precedence
                  enabled: update.admin_enabled === true
                    ? true
                    : (update.enabled !== undefined ? update.enabled : flag.enabled),
                  reason: update.admin_enabled === true
                    ? 'Admin override enabled'
                    : (update.admin_enabled === false && flag.admin_enabled)
                    ? 'Admin override disabled'
                    : (update.enabled !== undefined
                        ? (update.enabled ? 'Feature enabled' : 'Feature disabled')
                        : flag.effective_status.reason)
                }
              }
            : flag
        );
        queryClient.setQueryData([...featureFlagsKeys.list(), 'with-status', 'hi-si'], updatedFlagsWithStatus);
      }

      return { previousFlags, previousFlagsWithStatus };
    },
    onError: (error, _update, context) => {
      // Rollback optimistic updates
      if (context?.previousFlags) {
        queryClient.setQueryData(featureFlagsKeys.list(), context.previousFlags);
      }
      if (context?.previousFlagsWithStatus) {
        queryClient.setQueryData([...featureFlagsKeys.list(), 'with-status', 'hi-si'], context.previousFlagsWithStatus);
      }

      // DV-FF-002: Handle 409 conflicts by forcing a fresh refetch
      // This ensures UI shows the latest server state after conflict
      if (error && typeof error === 'object' && 'status' in error && error.status === 409) {
        // Immediately refetch to show current server state
        queryClient.invalidateQueries({ queryKey: featureFlagsKeys.all });
      }
    },
    onSettled: () => {
      // Always refetch to ensure consistency
      queryClient.invalidateQueries({ queryKey: featureFlagsKeys.all });
    },
  });
}

// Hook to create new feature flag
export function useCreateFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (flag: Omit<FeatureFlag, 'id' | 'created_at' | 'updated_at'>) =>
      featureFlagsAPI.createFlag(flag),
    onSuccess: () => {
      // Invalidate all flag queries
      queryClient.invalidateQueries({ queryKey: featureFlagsKeys.all });
    },
  });
}

// Hook to delete feature flag
export function useDeleteFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, reason }: { name: string; reason?: string }) =>
      featureFlagsAPI.deleteFlag(name, reason),
    onSuccess: () => {
      // Invalidate all flag queries
      queryClient.invalidateQueries({ queryKey: featureFlagsKeys.all });
    },
  });
}

// Hook to check admin access
export function useFeatureFlagsAdminCheck(): UseQueryResult<boolean, Error> {
  return useQuery({
    queryKey: [...featureFlagsKeys.all, 'admin-check'],
    queryFn: () => featureFlagsAPI.checkAdminAccess(),
    staleTime: 5 * 60 * 1000, // 5 minutes - admin status is stable
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: false, // Don't retry admin checks
  });
}